import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { createSseErrorReporter } from '@/utils/reportSseError';
import { GetStorageComponent } from '@/utils/session';
import { getEnv } from '@/utils/texts';

interface SyncedJob {
  [key: string]: unknown;
}

export interface FailedSyncJob {
  atsJobId: string;
  jobName: string;
  reason: string;
}

export interface IAtsSyncStreamState {
  jobs: SyncedJob[];
  jobsCreated: number;
  totalJobs: number;
  jobsRemaining: number;
  updatedJobNames: string[];
  closedJobIds: string[];
  candidatesAdded: { jobId: string; jobName: string; count: number }[];
  candidateSynced: {
    jobId: string;
    completed: number;
    total: number;
    globalCompleted: number;
    globalTotal: number;
    globalRemaining: number;
  } | null;
  candidateCompletedByJob: Record<string, number>;
  candidateUpdatedCount: number;
  candidatesPreparing: boolean;
  candidatesSynced: number | null;
  isInitializing: boolean;
  jobsComplete: boolean;
  isCompleted: boolean;
  failedJobs: FailedSyncJob[];
  error: string | null;
}

const initialState: IAtsSyncStreamState = {
  jobs: [],
  jobsCreated: 0,
  totalJobs: 0,
  jobsRemaining: 0,
  updatedJobNames: [],
  closedJobIds: [],
  candidatesAdded: [],
  candidateSynced: null,
  candidateCompletedByJob: {},
  candidateUpdatedCount: 0,
  candidatesPreparing: false,
  candidatesSynced: null,
  isInitializing: false,
  jobsComplete: false,
  isCompleted: false,
  failedJobs: [],
  error: null,
};

const storageKey = (id: string) => `ats-sync-stream-${id}`;

export const ACTIVE_SYNC_STORAGE_KEY = 'ats-active-sync-config-id';

const clearActiveFlag = (id: string) => {
  if (sessionStorage.getItem(ACTIVE_SYNC_STORAGE_KEY) === id) {
    sessionStorage.removeItem(ACTIVE_SYNC_STORAGE_KEY);
  }
};

// syncConfigId is per connection, not per sync run, so a re-sync on the same connection reuses
// it. Bump runKey to reconnect the stream for a new run with the same syncConfigId.
const useAtsSyncStream = (
  syncConfigId: string | null,
  enabled: boolean,
  runKey = 0,
): IAtsSyncStreamState & { reset: () => void } => {
  const [state, setState] = useState<IAtsSyncStreamState>(() => {
    if (!syncConfigId) return initialState;
    const stored = sessionStorage.getItem(storageKey(syncConfigId));
    if (!stored) return initialState;
    const parsed = { ...initialState, ...(JSON.parse(stored) as IAtsSyncStreamState) };
    if (parsed.isCompleted) {
      sessionStorage.removeItem(storageKey(syncConfigId));
      clearActiveFlag(syncConfigId);
      return initialState;
    }
    return parsed;
  });
  const abortRef = useRef<AbortController | null>(null);
  const restoredRef = useRef(false);

  useEffect(() => {
    if (!syncConfigId || !restoredRef.current || state.isCompleted) return;
    sessionStorage.setItem(storageKey(syncConfigId), JSON.stringify(state));
  }, [state, syncConfigId]);

  useEffect(() => {
    if (!enabled || !syncConfigId) return;

    const stored = sessionStorage.getItem(storageKey(syncConfigId));
    const restored = stored ? { ...initialState, ...(JSON.parse(stored) as IAtsSyncStreamState) } : initialState;
    // Resetting/restoring local state here is setup for the SSE subscription started just below
    // (keyed on the same syncConfigId), not state derivable from props during render.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setState(restored);
    restoredRef.current = true;

    if (restored.isCompleted) {
      sessionStorage.removeItem(storageKey(syncConfigId));
      clearActiveFlag(syncConfigId);
      return;
    }

    const token = GetStorageComponent('accessToken') ?? '';
    const idToken = GetStorageComponent('idToken') ?? '';
    const base = (getEnv('VITE_JOB_CAMPAIGN_BASE_URL') ?? '').replace(/\/$/, '');
    const url = `${base}/jobs/ats/sync/${syncConfigId}/stream`;
    const controller = new AbortController();
    const reportSseError = createSseErrorReporter({ feature: 'ats-sync', operation: 'sse-error' });
    abortRef.current = controller;

    void fetchEventSource(url, {
      method: 'GET',
      signal: controller.signal,
      openWhenHidden: true,
      headers: {
        Accept: 'text/event-stream',
        Authorization: token ? `Bearer ${token}` : '',
        ...(idToken ? { 'x-id-token': idToken } : {}),
        'Cache-Control': 'no-cache',
        Connection: 'keep-alive',
      },
      onopen: async response => {
        if (!response.ok) throw new Error(`SSE connection failed with status ${response.status}`);
      },
      onmessage: event => {
        if (event.event === 'sync_initializing') {
          const { totalJobs } = JSON.parse(event.data);
          setState(prev => ({ ...prev, totalJobs, isInitializing: true }));
          return;
        }

        if (event.event === 'job_detail_synced') {
          const { totalJobs, completedJobs } = JSON.parse(event.data);
          setState(prev => ({ ...prev, totalJobs, jobsCreated: completedJobs }));
          return;
        }

        if (event.event === 'jobs_complete') {
          const { totalJobsCreated } = JSON.parse(event.data);
          setState(prev => ({ ...prev, jobsCreated: totalJobsCreated, isInitializing: false, jobsComplete: true }));
          return;
        }

        if (event.event === 'candidates_preparing') {
          setState(prev => ({ ...prev, candidatesPreparing: true }));
          return;
        }

        if (event.event === 'candidates_added') {
          const { jobId, jobName, count } = JSON.parse(event.data);
          setState(prev => ({
            ...prev,
            candidatesPreparing: false,
            candidatesAdded: [...prev.candidatesAdded, { jobId, jobName, count }],
            updatedJobNames: jobName ? [...prev.updatedJobNames, jobName] : prev.updatedJobNames,
          }));
          return;
        }

        if (event.event === 'job_synced') {
          const { job, jobsCreated, totalJobs, jobsRemaining } = JSON.parse(event.data);
          setState(prev => ({
            ...prev,
            jobs: [...prev.jobs, job],
            jobsCreated,
            totalJobs,
            jobsRemaining,
          }));
          return;
        }

        if (event.event === 'candidate_synced') {
          const { jobId, completed, total, globalCompleted, globalTotal, globalRemaining } = JSON.parse(event.data);
          setState(prev => ({
            ...prev,
            candidatesPreparing: false,
            candidateSynced: { jobId, completed, total, globalCompleted, globalTotal, globalRemaining },
            candidateCompletedByJob: {
              ...prev.candidateCompletedByJob,
              [jobId]: (prev.candidateCompletedByJob[jobId] ?? 0) + 1,
            },
          }));
          return;
        }

        if (event.event === 'candidate_updated') {
          const { globalUpdated } = JSON.parse(event.data);
          setState(prev => ({ ...prev, candidateUpdatedCount: globalUpdated }));
          return;
        }

        if (event.event === 'job_sync_failed') {
          const { atsJobId, jobName, reason } = JSON.parse(event.data);
          setState(prev =>
            prev.failedJobs.some(job => job.atsJobId === atsJobId)
              ? prev
              : { ...prev, failedJobs: [...prev.failedJobs, { atsJobId, jobName, reason }] },
          );
          return;
        }

        if (event.event === 'sync_complete') {
          const { candidatesSynced } = JSON.parse(event.data);
          setState(prev => ({
            ...prev,
            isCompleted: true,
            error: null,
            candidatesPreparing: false,
            candidatesSynced: typeof candidatesSynced === 'number' ? candidatesSynced : prev.candidatesSynced,
          }));
          sessionStorage.removeItem(storageKey(syncConfigId));
          clearActiveFlag(syncConfigId);
          controller.abort();
          abortRef.current = null;
        }
      },
      onerror: error => {
        if (controller.signal.aborted) return;
        setState(prev => ({ ...prev, error: 'Lost connection to server.' }));
        reportSseError(error);
        // Returning (not throwing) tells fetchEventSource to keep retrying with its default
        // backoff instead of giving up permanently on a transient drop — AtsSyncToastListener
        // depends on this stream surviving long enough to see sync_complete.
      },
      onclose: () => {
        if (controller.signal.aborted) return;
        abortRef.current = null;
      },
    });

    return () => {
      controller.abort();
      abortRef.current = null;
    };
  }, [enabled, syncConfigId, runKey]);

  const reset = useCallback(() => setState(initialState), []);

  return { ...state, reset };
};

export default useAtsSyncStream;
